import type { User } from 'src/api/generated';
import { PropertyOperation } from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { trimToUndefined } from 'src/modules/shared/utils/formatting/text.util';
import type { PropertyActiveFilterItem } from '../components/molecules/property-active-filters';
import type {
  PropertyFiltersState,
  PropertyOperationFilter,
} from '../types/property-filters.types';
import type { PropertiesSearch } from '../schemas/properties-search.schema';

const operationFilterLabelKeyByValue: Record<
  Exclude<PropertiesSearch['operation'], 'ALL'>,
  TranslationKey
> = {
  SALE: 'catalog.properties.filters.operationSale',
  RENT: 'catalog.properties.filters.operationRent',
  TEMPORARY_RENT: 'catalog.properties.filters.operationTemporaryRent',
};

const activeStatusLabelKeyByValue: Record<
  Exclude<PropertiesSearch['activeStatus'], 'ACTIVE'>,
  TranslationKey
> = {
  INACTIVE: 'catalog.properties.filters.activeStatusInactive',
  ALL: 'catalog.properties.filters.activeStatusAll',
};

const operationQueryValueByFilter: Record<
  Exclude<PropertyOperationFilter, 'ALL'>,
  PropertyOperation | PropertyOperation[]
> = {
  SALE: [PropertyOperation.SALE, PropertyOperation.SALE_AND_RENT],
  RENT: [PropertyOperation.RENT, PropertyOperation.SALE_AND_RENT],
  TEMPORARY_RENT: PropertyOperation.TEMPORARY_RENT,
};

const getAssignedUserLabel = (
  userId: number,
  usersById: Map<
    number,
    {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      id: number;
      label?: string;
    }
  >,
  t: (key: TranslationKey) => string
): string => {
  if (userId === -1) {
    return t('catalog.properties.filters.assignedUnassigned');
  }

  const user = usersById.get(userId);
  if (user == null) {
    return String(userId);
  }

  const displayName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return displayName || (user.email ?? String(user.id));
};

export const formatRangeLabel = (
  label: string,
  from?: number,
  to?: number
): string | null => {
  if (typeof from !== 'number' && typeof to !== 'number') {
    return null;
  }

  const formatter = new Intl.NumberFormat('es-CL');
  const minLabel = typeof from === 'number' ? formatter.format(from) : '…';
  const maxLabel = typeof to === 'number' ? formatter.format(to) : '…';

  return `${label}: ${minLabel} - ${maxLabel}`;
};

export const getPriceSortField = (
  operation: PropertyOperationFilter
): 'salePriceBaseAmount' | 'rentPriceBaseAmount' =>
  operation === PropertyOperation.RENT ||
  operation === PropertyOperation.TEMPORARY_RENT
    ? 'rentPriceBaseAmount'
    : 'salePriceBaseAmount';

export const buildPropertiesQueryParams = (
  filters: PropertyFiltersState,
  pageSize: number
) => {
  const builder = createQuery().take(pageSize);
  const search = trimToUndefined(filters.search);
  const region = trimToUndefined(filters.region);
  const locality = trimToUndefined(filters.locality);
  const priceSortField = getPriceSortField(filters.operation);

  switch (filters.sortBy) {
    case 'CREATED_DESC':
      builder.orderByDesc('createdAt');
      break;
    case 'PRICE_DESC':
      builder.orderByDesc(priceSortField).orderByDesc('id');
      break;
    case 'PRICE_ASC':
      builder.orderByAsc(priceSortField).orderByAsc('id');
      break;
    case 'UPDATED_DESC':
      builder.orderByDesc('updatedAt');
      break;
  }

  if (search != null) {
    builder.search(search);
  }

  if (filters.operation !== 'ALL') {
    builder.equals('operation', operationQueryValueByFilter[filters.operation]);
  }

  if (filters.activeStatus === 'ACTIVE') {
    builder.equals('isActive', true);
  } else if (filters.activeStatus === 'INACTIVE') {
    builder.equals('isActive', false);
  }

  if (typeof filters.userId === 'number') {
    builder.equals('userId', filters.userId === -1 ? null : filters.userId);
  }

  if (typeof filters.salePriceMin === 'number') {
    builder.greaterThan('salePriceBaseAmount', filters.salePriceMin);
  }
  if (typeof filters.salePriceMax === 'number') {
    builder.lessThan('salePriceBaseAmount', filters.salePriceMax);
  }
  if (typeof filters.rentPriceMin === 'number') {
    builder.greaterThan('rentPriceBaseAmount', filters.rentPriceMin);
  }
  if (typeof filters.rentPriceMax === 'number') {
    builder.lessThan('rentPriceBaseAmount', filters.rentPriceMax);
  }
  if (region != null) {
    builder.equals('regionCode', region);
  }
  if (locality != null) {
    builder.equals('localityCode', locality);
  }

  return builder.build();
};

interface BuildActivePropertyFiltersParams {
  activeStatus: PropertiesSearch['activeStatus'];
  buildSearchHref: (nextSearch: PropertiesSearch) => string;
  locality?: string;
  localityLabelByCode: Map<string, string>;
  operation: PropertiesSearch['operation'];
  pageSize: number;
  phoneVersion: PropertiesSearch['phoneVersion'];
  region?: string;
  regionLabelByCode: Map<string, string>;
  rentPriceMax?: number;
  rentPriceMin?: number;
  salePriceMax?: number;
  salePriceMin?: number;
  searchTerm: string;
  sortBy: PropertiesSearch['sortBy'];
  t: (key: TranslationKey) => string;
  userId?: number;
  usersById: Map<number, User>;
  viewMode: PropertiesSearch['viewMode'];
}

export const buildActivePropertyFilterItems = (
  params: BuildActivePropertyFiltersParams
): PropertyActiveFilterItem[] => {
  const items: PropertyActiveFilterItem[] = [];
  const {
    activeStatus,
    buildSearchHref,
    locality,
    localityLabelByCode,
    operation,
    pageSize,
    phoneVersion,
    region,
    regionLabelByCode,
    rentPriceMax,
    rentPriceMin,
    salePriceMax,
    salePriceMin,
    searchTerm,
    sortBy,
    t,
    userId,
    usersById,
    viewMode,
  } = params;

  if (searchTerm.trim()) {
    items.push({
      id: 'search',
      label: `${t('catalog.properties.filters.searchLabel')}: ${searchTerm.trim()}`,
      href: buildSearchHref({
        phoneVersion,
        operation,
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        userId,
        salePriceMin,
        salePriceMax,
        rentPriceMin,
        rentPriceMax,
        region,
        locality,
      }),
    });
  }

  if (operation !== 'ALL') {
    items.push({
      id: 'operation',
      label: `${t('catalog.properties.filters.operationAll')}: ${t(
        operationFilterLabelKeyByValue[operation]
      )}`,
      href: buildSearchHref({
        phoneVersion,
        search: trimToUndefined(searchTerm),
        operation: 'ALL',
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        userId,
        salePriceMin,
        salePriceMax,
        rentPriceMin,
        rentPriceMax,
        region,
        locality,
      }),
    });
  }

  if (activeStatus !== 'ACTIVE') {
    items.push({
      id: 'activeStatus',
      label: `${t('catalog.properties.filters.activeStatusActive')}: ${t(
        activeStatusLabelKeyByValue[activeStatus]
      )}`,
      href: buildSearchHref({
        phoneVersion,
        search: trimToUndefined(searchTerm),
        operation,
        activeStatus: 'ACTIVE',
        sortBy,
        viewMode,
        pageSize,
        userId,
        salePriceMin,
        salePriceMax,
        rentPriceMin,
        rentPriceMax,
        region,
        locality,
      }),
    });
  }

  if (typeof userId === 'number') {
    items.push({
      id: 'userId',
      label: `${t('catalog.properties.table.assigned')}: ${getAssignedUserLabel(
        userId,
        usersById,
        t
      )}`,
      href: buildSearchHref({
        phoneVersion,
        search: trimToUndefined(searchTerm),
        operation,
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        salePriceMin,
        salePriceMax,
        rentPriceMin,
        rentPriceMax,
        region,
        locality,
      }),
    });
  }

  const saleRangeLabel = formatRangeLabel(
    t('catalog.properties.filters.salePriceRange'),
    salePriceMin,
    salePriceMax
  );
  if (saleRangeLabel != null) {
    items.push({
      id: 'salePrice',
      label: saleRangeLabel,
      href: buildSearchHref({
        phoneVersion,
        search: trimToUndefined(searchTerm),
        operation,
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        userId,
        rentPriceMin,
        rentPriceMax,
        region,
        locality,
      }),
    });
  }

  const rentRangeLabel = formatRangeLabel(
    t('catalog.properties.filters.rentPriceRange'),
    rentPriceMin,
    rentPriceMax
  );
  if (rentRangeLabel != null) {
    items.push({
      id: 'rentPrice',
      label: rentRangeLabel,
      href: buildSearchHref({
        phoneVersion,
        search: trimToUndefined(searchTerm),
        operation,
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        userId,
        salePriceMin,
        salePriceMax,
        region,
        locality,
      }),
    });
  }

  if (region) {
    items.push({
      id: 'region',
      label: `${t('catalog.properties.filters.region')}: ${
        regionLabelByCode.get(region) ?? region
      }`,
      href: buildSearchHref({
        phoneVersion,
        search: trimToUndefined(searchTerm),
        operation,
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        userId,
        salePriceMin,
        salePriceMax,
        rentPriceMin,
        rentPriceMax,
        locality,
      }),
    });
  }

  if (locality) {
    items.push({
      id: 'locality',
      label: `${t('catalog.properties.filters.locality')}: ${
        localityLabelByCode.get(locality) ?? locality
      }`,
      href: buildSearchHref({
        phoneVersion,
        search: trimToUndefined(searchTerm),
        operation,
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        userId,
        salePriceMin,
        salePriceMax,
        rentPriceMin,
        rentPriceMax,
        region,
      }),
    });
  }

  return items;
};
